import { Route } from '@angular/router';

import {
  AdminAuthGuard,
  ComingSoonComponent,
  mediaGuard,
  NotFoundComponent,
  unsavedChangesGuard,
} from '@rp/core';
import { ProfileTarget, TargetApp } from '@rp/shared/models';
import { tabsRouteMatcher } from '@rp/utils';
import { CommonMediaQueries } from '@rp/shared/services';

import { ShellComponent } from './shell/shell.component';

export const appRoutes: Route[] = [
  {
    path: 'login',
    loadComponent: () => import('./login').then(m => m.LoginComponent),
  },
  {
    path: 'storybook',
    loadComponent: () => import('@rp/storybook').then(m => m.StorybookComponent),
  },
  {
    path: '',
    component: ShellComponent,
    canActivate: [AdminAuthGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'statistic',
      },
      {
        path: 'partners',
        data: {
          title: 'headerTitle.partners',
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'webmasters',
          },
          {
            path: 'webmasters/:id',
            loadComponent: () =>
              import('@rp/pages/partners').then(m => m.PartnersWebmasterCardComponent),
            data: {
              title: 'pages.partners.cards.webmaster',
              showBack: true,
              backRoute: true,
            },
            canMatch: [mediaGuard(CommonMediaQueries.MD, true)],
          },
          {
            path: 'advertisers/:id',
            loadComponent: () =>
              import('@rp/pages/partners').then(m => m.PartnersAdvertiserCardComponent),
            data: {
              title: 'pages.partners.cards.advertiser',
              showBack: true,
              backRoute: true,
            },
            canMatch: [mediaGuard(CommonMediaQueries.MD, true)],
          },
          {
            matcher: tabsRouteMatcher,
            loadComponent: () => import('@rp/pages/partners').then(m => m.PartnersComponent),
          },
        ],
      },
      {
        path: 'rbac',
        loadComponent: () => import('@rp/pages/rbac').then(m => m.RoleBasedAccessControlComponent),
        data: {
          title: 'headerTitle.rbac',
        },
      },
      {
        path: 'faq',
        component: ComingSoonComponent,
      },
      {
        path: 'offers-wall',
        loadComponent: () => import('@rp/offers-wall-admin').then(m => m.OffersWallAdminComponent),
        data: {
          title: 'headerTitle.offersWall',
          header: 'general.webmasterId',
          target: ProfileTarget.Webmaster,
        },
      },
      {
        path: 'offers-wall/create',
        loadComponent: () => import('@rp/offers-wall-admin').then(m => m.CreateNewOfferComponent),
        data: {
          title: 'general.createOffer',
          showBack: true,
          backRoute: true,
        },
        canDeactivate: [unsavedChangesGuard],
      },
      {
        path: 'offers-wall/edit/:id',
        loadComponent: () => import('@rp/offers-wall-admin').then(m => m.EditOfferComponent),
        data: {
          title: 'general.editOffer',
          showBack: true,
        },
        canDeactivate: [unsavedChangesGuard],
      },
      {
        path: 'finance/view',
        loadComponent: () => import('@rp/pages/finance-admin').then(m => m.DetailsPageComponent),
        data: {
          title: 'menu.finance',
        },
      },
      {
        path: 'finance',
        data: {
          title: 'menu.finance',
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'payout-requests',
          },
          {
            matcher: tabsRouteMatcher,
            loadComponent: () =>
              import('@rp/pages/finance-admin').then(m => m.FinanceAdminComponent),
          },
        ],
      },
      {
        path: 'statistic',
        data: {
          target: TargetApp.Admin,
          title: 'menu.statistic',
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'general',
          },
          {
            path: 'general/:id',
            loadComponent: () =>
              import('@rp/pages/statistic').then(m => m.StatisticGeneralComponent),
            data: {
              title: 'statistic.statisticMainReport',
              target: TargetApp.Admin,
              showBack: true,
              backRoute: true,
            },
            canMatch: [mediaGuard(CommonMediaQueries.MD, true)],
          },
          {
            path: 'players/:id',
            loadComponent: () =>
              import('@rp/pages/statistic').then(m => m.StatisticPlayersComponent),
            data: {
              title: 'statistic.statisticMainReport',
              target: TargetApp.Admin,
              showBack: true,
              backRoute: true,
            },
            canMatch: [mediaGuard(CommonMediaQueries.MD, true)],
          },
          {
            matcher: tabsRouteMatcher,
            loadComponent: () => import('@rp/pages/statistic').then(m => m.StatisticComponent),
          },
        ],
      },
      {
        path: 'statistic/view',
        loadComponent: () => import('@rp/pages/statistic').then(m => m.StatisticDetailsComponent),
        data: {
          target: TargetApp.Admin,
        },
      },
      {
        path: 'promo',
        loadComponent: () => import('@rp/pages/promo').then(m => m.PromoComponent),
        data: {
          title: 'headerTitle.promo',
        },
      },
      {
        path: 'streams',
        loadComponent: () => import('@rp/pages/streams').then(m => m.StreamsComponent),
        data: {
          title: 'headerTitle.streams',
        },
      },
      {
        path: 'streams/:id',
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'details',
          },
          {
            matcher: tabsRouteMatcher,
            loadComponent: () => import('@rp/pages/streams').then(m => m.StreamInfoComponent),
            data: {
              title: 'headerTitle.streamDetails',
              showBack: true,
            },
          },
          {
            path: 'details',
            loadComponent: () => import('@rp/pages/streams').then(m => m.StreamDetailsComponent),
          },
          {
            path: 'leads',
            loadComponent: () => import('@rp/pages/streams').then(m => m.StreamLeadsComponent),
          },
        ],
      },
      {
        path: 'promo/create',
        loadComponent: () => import('@rp/pages/promo').then(m => m.PromoCreateCardComponent),
        data: {
          title: 'headerTitle.promoCreate',
          showBack: true,
          backRoute: true,
        },
        canDeactivate: [unsavedChangesGuard],
      },
      {
        path: 'promo/edit/:id',
        loadComponent: () => import('@rp/pages/promo').then(m => m.PromoEditCardComponent),
        data: {
          title: 'headerTitle.promoEdit',
          showBack: true,
        },
        canDeactivate: [unsavedChangesGuard],
      },
      {
        path: 'advertisers',
        loadComponent: () => import('@rp/pages/advertisers').then(m => m.AdvertisersComponent),
        data: {
          title: 'headerTitle.advertisers',
        },
      },
      {
        path: 'advertisers/create',
        loadComponent: () => import('@rp/pages/advertisers').then(m => m.AdvertiserCreateComponent),
        data: {
          title: 'headerTitle.advertisersCreate',
          showBack: true,
          backRoute: true,
        },
        canDeactivate: [unsavedChangesGuard],
      },
      {
        path: 'advertisers/edit/:id',
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'advertiser-info',
          },
          {
            matcher: tabsRouteMatcher,
            loadComponent: () =>
              import('@rp/pages/advertisers').then(m => m.AdvertiserEditComponent),
            data: {
              showBack: true,
            },
            canDeactivate: [unsavedChangesGuard],
          },
          {
            path: 'documents/contracts/create',
            loadComponent: () =>
              import('@rp/pages/advertisers').then(
                m => m.AdvertiserDocumentContractsCreateComponent,
              ),
            data: {
              showBack: true,
              backRoute: true,
            },
            canDeactivate: [unsavedChangesGuard],
          },
          {
            path: 'documents/contracts/edit/:contractId',
            loadComponent: () =>
              import('@rp/pages/advertisers').then(m => m.AdvertiserDocumentContractsEditComponent),
            data: {
              showBack: true,
              backRoute: true,
            },
            canDeactivate: [unsavedChangesGuard],
          },
          {
            path: 'documents/bills/create',
            loadComponent: () =>
              import('@rp/pages/advertisers').then(m => m.AdvertiserDocumentBillsCreateComponent),
            data: {
              showBack: true,
              backRoute: true,
            },
            canDeactivate: [unsavedChangesGuard],
          },
          {
            path: 'documents/bills/edit/:billId',
            loadComponent: () =>
              import('@rp/pages/advertisers').then(m => m.AdvertiserDocumentBillsEditComponent),
            data: {
              showBack: true,
              backRoute: true,
            },
            canDeactivate: [unsavedChangesGuard],
          },
          {
            path: 'documents/verifications/create',
            loadComponent: () =>
              import('@rp/pages/advertisers').then(
                m => m.AdvertiserDocumentVerificationsCreateComponent,
              ),
            data: {
              showBack: true,
              backRoute: true,
            },
            canDeactivate: [unsavedChangesGuard],
          },
          {
            path: 'documents/verifications/edit/:verificationId',
            loadComponent: () =>
              import('@rp/pages/advertisers').then(
                m => m.AdvertiserDocumentVerificationsEditComponent,
              ),
            data: {
              showBack: true,
              backRoute: true,
            },
            canDeactivate: [unsavedChangesGuard],
          },
        ],
      },
      {
        path: '**',
        component: NotFoundComponent,
      },
    ],
  },
];
